import type { SerializedError } from '@reduxjs/toolkit';
import type { ErrorResponse } from '@talos/kyoko';
import type { CoinMetricsError } from './coinMetrics.types';

/**
 * CoinMetrics error `type` tokens that indicate the configured API key itself is missing or
 * invalid (HTTP 401) — `unauthorized` is included defensively in case an endpoint reports this
 * differently. Deliberately excludes `forbidden` (HTTP 403): per CoinMetrics' API conventions, a
 * 403 means the key is valid but not entitled to the specific requested resource (e.g. a metric
 * outside the org's plan), not that the key is bad. Misclassifying that as an invalid key would
 * flip the global "API key invalid" state from a single unentitled metric and blank out every
 * Intel module app-wide, even though every other request is succeeding.
 */
const AUTH_ERROR_TYPES = new Set(['wrong_credentials', 'unauthorized']);

/** CoinMetrics error `type` for a per-resource entitlement gap (HTTP 403) — see `isCoinMetricsForbiddenError`. */
const FORBIDDEN_ERROR_TYPES = new Set(['forbidden']);

/**
 * True when a failed CoinMetrics request means the org's `ui.CoinMetricsApiKey` itself is
 * missing/invalid (HTTP 401) — as opposed to a per-resource entitlement gap (HTTP 403), which is a
 * normal per-query error and must not trip the global "invalid key" state. This case is
 * unrecoverable (retrying with the same key never succeeds), so callers use it both to skip
 * retries and to surface a dedicated error message.
 *
 * Matches on the CM error `type` first (authoritative, status-independent); falls back to the
 * HTTP status only when no envelope was parsed (e.g. a gateway error with no JSON body).
 */
export function isCoinMetricsAuthError(error: CoinMetricsError): boolean {
  if (error.type != null) {
    return AUTH_ERROR_TYPES.has(error.type);
  }
  return error.status === 401;
}

/**
 * True when a failed CoinMetrics request is a per-resource entitlement gap (HTTP 403, CM type
 * `forbidden`) — the key is valid but not entitled to the specific metric/resource requested, as
 * opposed to `isCoinMetricsAuthError`'s invalid-key case. Unlike an invalid key, this doesn't affect
 * the global "API key invalid" state, but it's just as deterministic: retrying with the same key can
 * never succeed. Used to bail the retry wrapper immediately instead of burning
 * `COIN_METRICS_MAX_RETRIES` backoff attempts before the permission message renders.
 *
 * Matches on the CM error `type` first (authoritative, status-independent); falls back to the HTTP
 * status only when no envelope was parsed (e.g. a gateway error with no JSON body).
 */
export function isCoinMetricsForbiddenError(error: CoinMetricsError): boolean {
  if (error.type != null) {
    return FORBIDDEN_ERROR_TYPES.has(error.type);
  }
  return error.status === 403;
}

/**
 * True when a CoinMetrics RTK Query error response is a per-resource entitlement gap (HTTP 403, CM
 * type `forbidden`) — the key is valid but not entitled to the specific metric/resource that
 * failed. Errors surfaced to callers via RTK Query are the flattened kyoko `ErrorResponse` shape
 * (`toErrorResponse` in `coinMetricsEndpoints.ts`), which folds the original CoinMetrics error
 * `type` into a `[type] message` prefix rather than keeping it as a separate field, so this checks
 * for that prefix rather than a `type` property. Useful for queries with no catalog-based
 * eligibility pre-check of their own (e.g. Historical Funding Rate's price overlay, which always
 * fetches directly for the selected asset), where a `forbidden` response on the query itself is the
 * only signal that a specific metric isn't entitled.
 *
 * Accepts `SerializedError` alongside `ErrorResponse` because that's the union RTK Query's generated
 * `error` field carries — a `queryFn` throw gets wrapped as `SerializedError` rather than reaching
 * `toErrorResponse`. Both shapes carry an optional `message`, which is all this needs.
 */
export function isForbiddenCoinMetricsError(error: ErrorResponse | SerializedError | undefined): boolean {
  return error?.message?.startsWith('[forbidden]') ?? false;
}
